import type { SxProps, Theme } from "@mui/material";
import cx from "classnames";
import ArrowIcon, { type ArrowIconProps } from "../ArrowIcon/ArrowIcon";

const SIZES = {
  small: 9,
  medium: 12,
};

const ARROW_ICON_SX: SxProps<Theme> = {
  padding: 0.1,
  cursor: "pointer",
  color: "text.secondary",
  "&:hover": {
    color: "primary.main",
  },
};

const DISABLED_ARROW_SX = { pointerEvents: "none", opacity: 0.3 } as const;

const MOUSE_DOWN_CAPTURE_HANDLER = (e: React.MouseEvent<SVGSVGElement>) =>
  e.stopPropagation();

interface UpDownArrowsProps {
  className?: string;
  onUpClick: (e: React.MouseEvent<SVGSVGElement>) => void;
  onDownClick: (e: React.MouseEvent<SVGSVGElement>) => void;
  size?: "small" | "medium";
  /** Current value — used with `min`/`max` to disable arrows at bounds */
  value?: number;
  min?: number;
  max?: number;
  slotProps?: {
    up?: ArrowIconProps & { "data-test-id"?: string };
    down?: ArrowIconProps & { "data-test-id"?: string };
  };
}

export default function UpDownArrows({
  className,
  onUpClick,
  onDownClick,
  size = "small",
  value,
  min,
  max,
  slotProps,
}: UpDownArrowsProps) {
  const atMax = value !== undefined && max !== undefined && value >= max;
  const atMin = value !== undefined && min !== undefined && value <= min;

  return (
    <div className={cx("flex flex-col gap-0", className)}>
      <ArrowIcon
        up
        size={SIZES[size]}
        onClick={(e) => !atMax && onUpClick(e)}
        onMouseDownCapture={MOUSE_DOWN_CAPTURE_HANDLER} // required to prevent the parent from being clicked, for exmaple if this is used within a MenuItem
        {...slotProps?.up}
        sx={
          {
            ...ARROW_ICON_SX,
            ...slotProps?.up?.sx,
            ...(atMax ? DISABLED_ARROW_SX : undefined),
          } as SxProps<Theme>
        }
      />
      <ArrowIcon
        down
        size={SIZES[size]}
        onClick={(e) => !atMin && onDownClick(e)}
        onMouseDownCapture={MOUSE_DOWN_CAPTURE_HANDLER}
        {...slotProps?.down}
        sx={
          {
            ...ARROW_ICON_SX,
            ...slotProps?.down?.sx,
            ...(atMin ? DISABLED_ARROW_SX : undefined),
          } as SxProps<Theme>
        }
      />
    </div>
  );
}
